import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Modal } from 'react-bootstrap';
import { onValue, push, ref, set } from 'firebase/database';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';

interface LearningCard {
  id: string;
  frontText?: string;
  frontImageUrl?: string;
  backText?: string;
  backImageUrl?: string;
  rating?: number;
  createdAt?: number;
}

const preloadedImagePromises = new Map<string, Promise<void>>();

const preloadImage = (url: string) => {
  const cleaned = (url || '').trim();
  if (!cleaned) return;
  if (preloadedImagePromises.has(cleaned)) return;

  const promise = new Promise<void>((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = cleaned;

    const decode = (img as any).decode;
    if (typeof decode === 'function') {
      (img as any)
        .decode()
        .then(() => resolve())
        .catch(() => resolve());
    }
  });

  preloadedImagePromises.set(cleaned, promise);
};

const shuffleArray = <T,>(arr: T[]): T[] => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const LearningCardStudy = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setId } = useParams();

  const startOnBackFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get('startOnBack') || '').toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes';
  }, [location.search]);

  const minRatingFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('minRating');
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (n < 1 || n > 5) return null;
    return Math.floor(n);
  }, [location.search]);

  const cardIdsFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('cardIds');
    if (!raw) return null;
    return raw.split(',').filter(Boolean);
  }, [location.search]);

  const [setName, setSetName] = useState<string>('');
  const [cards, setCards] = useState<LearningCard[]>([]);
  const [shuffled, setShuffled] = useState(true);
  const [shuffleNonce, setShuffleNonce] = useState(0);
  const [startOnBack, setStartOnBack] = useState(startOnBackFromQuery);
  const [index, setIndex] = useState(0);
  const [showBack, setShowBack] = useState(startOnBackFromQuery);
  const [sessionStartedAt, setSessionStartedAt] = useState<number>(() => Date.now());
  const [answersByCardId, setAnswersByCardId] = useState<Record<string, 'known' | 'unknown'>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [sessionFinishedAt, setSessionFinishedAt] = useState<number | null>(null);
  const [savedSessionId, setSavedSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!setId) return;
    setShuffled(true);
  }, [setId]);

  useEffect(() => {
    setStartOnBack(startOnBackFromQuery);
  }, [setId, startOnBackFromQuery]);

  useEffect(() => {
    if (!currentUser || !setId) return;

    const metaRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/name`);
    const unsubMeta = onValue(metaRef, (snapshot) => {
      setSetName(snapshot.val() || '');
    });

    const cardsRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards`);
    const unsubCards = onValue(cardsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data).map(([id, value]: [string, any]) => ({
        id,
        frontText: value?.frontText,
        frontImageUrl: value?.frontImageUrl,
        backText: value?.backText,
        backImageUrl: value?.backImageUrl,
        rating: value?.rating,
        createdAt: value?.createdAt
      }));
      list.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
      let filtered = list;
      if (cardIdsFromQuery && cardIdsFromQuery.length > 0) {
        filtered = list.filter((card) => cardIdsFromQuery.includes(card.id));
      } else if (minRatingFromQuery !== null) {
        filtered = list.filter((card) => (card.rating || 0) >= minRatingFromQuery);
      }
      setCards(filtered);
      setIndex(0);
      setShowBack(startOnBack);
    });

    return () => {
      unsubMeta();
      unsubCards();
    };
  }, [currentUser, setId, startOnBack, minRatingFromQuery, cardIdsFromQuery]);

  const limitValue = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('limit');
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    if (n <= 0) return null;
    return Math.floor(n);
  }, [location.search]);

  const orderedCards = useMemo(() => {
    void shuffleNonce;
    const base = shuffled ? shuffleArray(cards) : cards;
    if (!limitValue || cardIdsFromQuery) return base;
    return base.slice(0, limitValue);
  }, [cards, limitValue, shuffled, shuffleNonce, cardIdsFromQuery]);

  useEffect(() => {
    setIndex(0);
    setShowBack(startOnBack);
    setSessionStartedAt(Date.now());
    setAnswersByCardId({});
    setSaveError(null);
  }, [limitValue, shuffled, shuffleNonce, startOnBack, cardIdsFromQuery]);

  useEffect(() => {
    setShowBack(startOnBack);
  }, [startOnBack]);

  const current = orderedCards[index];

  const canGoPrev = index > 0;
  const canGoNext = index < orderedCards.length - 1;

  const isOnSecondSide = startOnBack ? !showBack : showBack;

  const knownCount = useMemo(() => {
    return Object.values(answersByCardId).filter((v) => v === 'known').length;
  }, [answersByCardId]);

  const unknownCount = useMemo(() => {
    return Object.values(answersByCardId).filter((v) => v === 'unknown').length;
  }, [answersByCardId]);

  const stats = useMemo(() => {
    const total = orderedCards.length;
    const known = knownCount;
    const unknown = unknownCount;
    const unanswered = Math.max(0, total - (known + unknown));
    const pctKnown = total > 0 ? Math.round((known / total) * 100) : 0;
    const pctUnknown = total > 0 ? Math.round((unknown / total) * 100) : 0;
    const finishedAt = sessionFinishedAt ?? Date.now();
    const durationMs = Math.max(0, finishedAt - sessionStartedAt);
    const durationSec = Math.floor(durationMs / 1000);
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;

    return {
      total,
      known,
      unknown,
      unanswered,
      pctKnown,
      pctUnknown,
      minutes,
      seconds
    };
  }, [knownCount, orderedCards.length, sessionFinishedAt, sessionStartedAt, unknownCount]);

  const saveAndShowStats = useCallback(async (answers: Record<string, 'known' | 'unknown'>) => {
    if (!currentUser || !setId) return;
    if (isSaving) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const finishedAt = Date.now();

      const known = Object.values(answers).filter((v) => v === 'known').length;
      const unknown = Object.values(answers).filter((v) => v === 'unknown').length;
      const answered = known + unknown;

      const historyRef = ref(database, `learning_cards/${currentUser.uid}/history`);
      const newRef = push(historyRef);

      await set(newRef, {
        setId,
        setName: setName || null,
        startedAt: sessionStartedAt,
        finishedAt,
        totalCards: orderedCards.length,
        answeredCount: answered,
        knownCount: known,
        unknownCount: unknown,
        shuffled,
        startOnBack,
        limit: limitValue ?? null,
        minRating: minRatingFromQuery ?? null,
        cardIds: cardIdsFromQuery ?? null,
        cardOrder: orderedCards.map((c) => c.id),
        answersByCardId: answers
      });

      setAnswersByCardId(answers);
      setSavedSessionId(newRef.key);
      setSessionFinishedAt(finishedAt);
      setShowStatsModal(true);
    } catch (e) {
      console.error(e);
      setSaveError('Fehler beim Speichern der Lern-Resultate.');
    } finally {
      setIsSaving(false);
    }
  }, [
    currentUser,
    isSaving,
    limitValue,
    orderedCards,
    sessionStartedAt,
    setId,
    setName,
    shuffled,
    startOnBack,
    minRatingFromQuery,
    cardIdsFromQuery
  ]);

  const markAnswerAndAdvance = useCallback(
    (answer: 'known' | 'unknown') => {
      if (!current) return;

      const nextAnswers = {
        ...answersByCardId,
        [current.id]: answer
      };

      setAnswersByCardId(nextAnswers);

      if (canGoNext) {
        setIndex((i) => i + 1);
        setShowBack(startOnBack);
        return;
      }

      saveAndShowStats(nextAnswers);
    },
    [answersByCardId, canGoNext, current, saveAndShowStats, startOnBack]
  );

  useEffect(() => {
    const PRELOAD_AHEAD = 4;
    const urls: string[] = [];

    for (let i = index; i <= index + PRELOAD_AHEAD; i += 1) {
      const c = orderedCards[i];
      if (!c) break;
      if ((c.frontImageUrl || '').trim()) urls.push(c.frontImageUrl as string);
      if ((c.backImageUrl || '').trim()) urls.push(c.backImageUrl as string);
    }

    urls.forEach(preloadImage);
  }, [index, orderedCards]);

  const handleFinish = async () => {
    saveAndShowStats(answersByCardId);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.code !== 'KeyN' && e.code !== 'KeyY') return;

      const target = e.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) return;

      e.preventDefault();

      if (showBack) {
        if (e.code === 'Space' || e.code === 'KeyY') {
          markAnswerAndAdvance('known');
          return;
        }

        if (e.code === 'KeyN') {
          markAnswerAndAdvance('unknown');
          return;
        }
      }

      if (e.code !== 'Space') return;

      const startSide = startOnBack ? 'back' : 'front';
      const isCurrentlyStartSide = startSide === 'back' ? showBack : !showBack;

      if (isCurrentlyStartSide) {
        setShowBack((v) => !v);
        return;
      }

      if (canGoNext) {
        setIndex((i) => i + 1);
        setShowBack(startOnBack);
        return;
      }

      setShowBack(startOnBack);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoNext, markAnswerAndAdvance, showBack, startOnBack]);

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Learning Cards</h2>
        <Card>
          <Card.Body>
            <p>Bitte melden Sie sich an, um Learning Cards zu verwenden.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!setId) {
    return (
      <Container>
        <Alert variant="danger">Set-ID fehlt.</Alert>
      </Container>
    );
  }

  if (orderedCards.length === 0) {
    return (
      <Container>
        <div className="mb-2">
          <Link to={`/dashboard/learning-cards/${setId}`} style={{ textDecoration: 'none' }}>
            ← Zurück
          </Link>
        </div>
        <Alert variant="info">
          Keine Karten in diesem Set.
        </Alert>
      </Container>
    );
  }

  const frontHasContent = (current.frontText || '').trim() || (current.frontImageUrl || '').trim();
  const backHasContent = (current.backText || '').trim() || (current.backImageUrl || '').trim();

  return (
    <Container>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="m-0">{setName || 'Lernen'}</h2>
          <div>
            <Link to={`/dashboard/learning-cards/${setId}`} style={{ textDecoration: 'none' }}>
              ← Zurück zum Set
            </Link>
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setIndex(0);
              setShowBack(startOnBack);
              setSessionStartedAt(Date.now());
              setAnswersByCardId({});
              setSaveError(null);
            }}
          >
            Reset
          </Button>
          <Button
            variant={shuffled ? 'success' : 'outline-success'}
            onClick={() => {
              if (limitValue) {
                setShuffled(true);
                setShuffleNonce((v) => v + 1);
                return;
              }

              setShuffled((v) => !v);
            }}
          >
            Shuffle
          </Button>
          <Button
            variant={startOnBack ? 'success' : 'outline-success'}
            onClick={() => setStartOnBack((v) => !v)}
          >
            Rückseite zuerst
          </Button>
        </div>
      </div>

      <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
        Karte {index + 1} / {orderedCards.length}
      </div>

      <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
        Gewusst: {knownCount} | Nicht gewusst: {unknownCount}
      </div>

      {saveError && (
        <Alert variant="danger" className="mb-3">
          {saveError}
        </Alert>
      )}

      <Modal
        show={showStatsModal}
        onHide={() => {
          setShowStatsModal(false);
          navigate('/dashboard/learning-cards');
        }}
        fullscreen="sm-down"
        scrollable
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Session Statistik</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-bold mb-2">{setName || 'Learning Cards'}</div>
          <div className="mb-2">Dauer: {stats.minutes}:{String(stats.seconds).padStart(2, '0')}</div>
          <div className="mb-2">Total Karten: {stats.total}</div>
          <div className="mb-2">Gewusst: {stats.known} ({stats.pctKnown}%)</div>
          <div className="mb-2">Nicht gewusst: {stats.unknown} ({stats.pctUnknown}%)</div>
          <div className="mb-2">Nicht beantwortet: {stats.unanswered}</div>
          {savedSessionId && (
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
              Session: {savedSessionId}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {stats.unknown > 0 && (
            <Button
              variant="outline-warning"
              onClick={() => {
                const unknownCardIds = Object.entries(answersByCardId)
                  .filter(([, answer]) => answer === 'unknown')
                  .map(([cardId]) => cardId);
                setShowStatsModal(false);
                const params = new URLSearchParams();
                params.set('cardIds', unknownCardIds.join(','));
                if (startOnBack) params.set('startOnBack', '1');
                navigate(`/dashboard/learning-cards/${setId}/study?${params.toString()}`);
              }}
            >
              Nicht gewusste wiederholen
            </Button>
          )}
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowStatsModal(false);
              navigate('/dashboard/learning-cards/history');
            }}
          >
            Learning History
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowStatsModal(false);
              navigate('/dashboard/learning-cards');
            }}
          >
            Zur Übersicht
          </Button>
        </Modal.Footer>
      </Modal>

      <Card
        onClick={() => setShowBack((v) => !v)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          border: '2px solid #1780a6'
        }}
      >
        <Card.Body style={{ minHeight: '320px' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold">{showBack ? 'Lösung' : 'Frage'}</div>
            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
              Klick zum Umdrehen
            </div>
          </div>

          {!showBack ? (
            <div>
              {frontHasContent ? (
                <>
                  {(current.frontText || '').trim() && (
                    <div style={{ fontSize: '1.2rem', whiteSpace: 'pre-wrap' }}>{current.frontText}</div>
                  )}
                  {(current.frontImageUrl || '').trim() && (
                    <img
                      src={current.frontImageUrl}
                      alt="front"
                      loading="eager"
                      decoding="async"
                      style={{ maxWidth: '100%', maxHeight: '220px', objectFit: 'contain', display: 'block', marginTop: '12px' }}
                    />
                  )}
                </>
              ) : (
                <div className="text-muted">Keine Inhalte auf der Vorderseite.</div>
              )}
            </div>
          ) : (
            <div>
              {backHasContent ? (
                <>
                  {(current.backText || '').trim() && (
                    <div style={{ fontSize: '1.2rem', whiteSpace: 'pre-wrap' }}>{current.backText}</div>
                  )}
                  {(current.backImageUrl || '').trim() && (
                    <img
                      src={current.backImageUrl}
                      alt="back"
                      loading="eager"
                      decoding="async"
                      style={{ maxWidth: '100%', maxHeight: '220px', objectFit: 'contain', display: 'block', marginTop: '12px' }}
                    />
                  )}
                </>
              ) : (
                <div className="text-muted">Keine Inhalte auf der Rückseite.</div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="mt-3">
        <div className="d-none d-md-flex justify-content-between">
          <Button
            variant="outline-primary"
            onClick={() => {
              if (!canGoPrev) return;
              setIndex((i) => i - 1);
              setShowBack(startOnBack);
            }}
            disabled={!canGoPrev}
          >
            ← Zurück
          </Button>

          <div className="d-flex gap-2">
            {isOnSecondSide && (
              <>
                <Button
                  variant="outline-danger"
                  onClick={() => markAnswerAndAdvance('unknown')}
                  disabled={isSaving}
                >
                  Nicht gewusst
                </Button>
                <Button
                  variant="success"
                  onClick={() => markAnswerAndAdvance('known')}
                  disabled={isSaving}
                >
                  Gewusst
                </Button>
              </>
            )}

            {isOnSecondSide && !canGoNext ? (
              <Button variant="primary" onClick={handleFinish} disabled={isSaving}>
                Finish (ohne Bewertung)
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  if (!canGoNext) return;
                  setIndex((i) => i + 1);
                  setShowBack(startOnBack);
                }}
                disabled={!canGoNext}
              >
                Weiter →
              </Button>
            )}
          </div>
        </div>

        <div className="d-flex d-md-none flex-column gap-2">
          {isOnSecondSide && (
            <div
              className="gap-2"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr'
              }}
            >
              <Button
                variant="outline-danger"
                onClick={() => markAnswerAndAdvance('unknown')}
                disabled={isSaving}
                className="py-4"
              >
                Nicht gewusst
              </Button>
              <Button
                variant="success"
                onClick={() => markAnswerAndAdvance('known')}
                disabled={isSaving}
                className="py-4"
              >
                Gewusst
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => {
                  if (!canGoPrev) return;
                  setIndex((i) => i - 1);
                  setShowBack(startOnBack);
                }}
                disabled={!canGoPrev}
              >
                ← Zurück
              </Button>
              {isOnSecondSide && !canGoNext ? (
                <Button variant="primary" onClick={handleFinish} disabled={isSaving}>
                  Finish
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!canGoNext) return;
                    setIndex((i) => i + 1);
                    setShowBack(startOnBack);
                  }}
                  disabled={!canGoNext}
                >
                  Weiter →
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default LearningCardStudy;
